// Controlled filter bar for the inventory list.
//
// Component is dumb — the parent owns filter state. That way the
// dealer-switcher (admin only) can sit next to the filters without
// leaking state between them.
import React from 'react';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  CATEGORY_OPTIONS, CONDITION_OPTIONS,
  AVAILABILITY_OPTIONS, FRESHNESS_OPTIONS, CONFIDENCE_OPTIONS,
  AGENT_USABLE_OPTIONS,
} from './constants';

// Radix UI Select rejects empty-string values on SelectItem (reserved
// for placeholder). Filters carry "" externally to mean "no filter";
// translate to/from __all__ at the Select boundary so the parent's
// filter object semantics stay unchanged.
const ALL = '__all__';
const toUI = (v) => (v == null || v === '' ? ALL : v);
const fromUI = (v) => (v === ALL ? '' : v);

export default function InventoryFilters({ filters, onChange }) {
  const set = (key) => (value) => onChange({ ...filters, [key]: fromUI(value) });

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
      <div className="md:col-span-2">
        <Input
          placeholder="Search VIN, stock #, make, model, trim…"
          value={filters.search || ''}
          onChange={(e) => onChange({ ...filters, search: e.target.value })}
        />
      </div>
      <Select value={toUI(filters.category)} onValueChange={set('category')}>
        <SelectTrigger><SelectValue placeholder="Category" /></SelectTrigger>
        <SelectContent>
          {CATEGORY_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={toUI(filters.condition)} onValueChange={set('condition')}>
        <SelectTrigger><SelectValue placeholder="Condition" /></SelectTrigger>
        <SelectContent>
          {CONDITION_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={toUI(filters.availability_status)}
        onValueChange={set('availability_status')}
      >
        <SelectTrigger><SelectValue placeholder="Availability" /></SelectTrigger>
        <SelectContent>
          {AVAILABILITY_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={toUI(filters.freshness_status)}
        onValueChange={set('freshness_status')}
      >
        <SelectTrigger><SelectValue placeholder="Freshness" /></SelectTrigger>
        <SelectContent>
          {FRESHNESS_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={toUI(filters.confidence_level)}
        onValueChange={set('confidence_level')}
      >
        <SelectTrigger><SelectValue placeholder="Confidence" /></SelectTrigger>
        <SelectContent>
          {CONFIDENCE_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={toUI(filters.agent_usable)}
        onValueChange={set('agent_usable')}
      >
        <SelectTrigger><SelectValue placeholder="Agent usability" /></SelectTrigger>
        <SelectContent>
          {AGENT_USABLE_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        placeholder="Make (exact)"
        value={filters.make || ''}
        onChange={(e) => set('make')(e.target.value)}
      />
      <Input
        placeholder="Model (exact)"
        value={filters.model || ''}
        onChange={(e) => set('model')(e.target.value)}
      />
    </div>
  );
}
